import isNil from 'lodash/isNil'
import get from 'lodash/get'
import { VideoMediaCenterMenuBaseColumn } from './video-media-center-menu-base-column'
import { SubtitlesMenuState } from './subtitles-menu-state'
import { AUTO_MODE_VALUE, OFF_MODE_VALUE } from './subtitles-menu-mode'

import { PLUGIN_NAME } from './plugin-name'
import { HAVE_METADATA } from './html-media-ready-states'

const { videojs } = global

const CaptionSettingsMenuItem = videojs.getComponent('CaptionSettingsMenuItem')
const MenuItem = videojs.getComponent('MenuItem')

/**
 * @class
 * @extends VideoMediaCenterMenuBaseColumn
 */
// eslint-disable-next-line import/prefer-default-export
export class VideoMediaCenterMenuSubtitlesColumn extends VideoMediaCenterMenuBaseColumn {
  #pluginAPI

  #textTracks

  #menuState

  /**
   * @param {Player|Object} player
   * @param {Object} [options]
   * @returns {void}
   */
  constructor (player, options) {
    super(player, options)

    this.#pluginAPI = this.player()[PLUGIN_NAME]()
    this.#textTracks = this.#getTextTracks()

    this.#menuState = new SubtitlesMenuState({
      userLanguages: this.#pluginAPI.getUserLanguages(),
      restoredValue: this.#pluginAPI.getRestoredActiveTextTrack(),
      appLanguage: this.#pluginAPI.getAppLanguage(),
      audioTrackLanguage: this.#pluginAPI.getRestoredActiveAudioTrack(),
      textTracks: this.#textTracks,
    })

    this.addTitle(this.#title)

    this.#addCaptionsSettings()
    this.#addAutoItem()
    this.#addOffItem()

    const items = this.#getItems()
    if (items.length > 0) {
      this.addItems(items)
    }

    /**
     * videojs uses native TextTracks in Safari, while in some
     * other browsers it uses synthetic ones.
     * This leads to slightly different lifecycles.
     * The 'if' below is a way to make sure that we run
     * initialization when we have all the required metadata,
     * regardless of the origin of the TextTracks
     */
    if (this.player().readyState() >= HAVE_METADATA) {
      this.#updateSelection()
    } else {
      this.player().on('loadedmetadata', () => {
        this.#updateSelection()
      })
    }
  }

  #title = 'subtitles'

  textTracks = []

  /**
   * get list of available text tracks
   * @returns {string[]}
   */
  #getTextTracks () {
    const textTracksDetails = this.#pluginAPI.getTextTracks()

    const textTracksTypeList = textTracksDetails.keySeq().toJS()

    const textTracks = textTracksTypeList
      .filter((textTrackType) => textTrackType !== 'thumbnail' && textTrackType !== 'chapters')
      .reduce((acc, textTrackType) => {
        /**
         * NOTE this is how it works before.
         * Currently it's not clear how to distinguish subtitles and captions
         * The future solution will be add '(cc)' to subtitles with captions
         */
        const textTrackLanuages = textTracksDetails
          .get(textTrackType)
          .keySeq()
          .toJS()

        return [...acc, ...textTrackLanuages]
      }, [])

    return textTracks
  }

  /**
   * generate menu items
   * @returns {MenuItem[]}
   */
  #getItems () {
    const textTracks = this.#textTracks

    const menuItems = textTracks.map((textTrackValue) => {
      const menuItemOptions = {
        selectable: true,
        multiSelectable: false,
        value: textTrackValue,
        label: this.#pluginAPI.getLanguageLabel(textTrackValue),
        selected: false,
      }
      const menuItem = new MenuItem(this.player(), menuItemOptions)
      return menuItem
    })
    return menuItems
  }

  /**
   * @returns {void}
   */
  #addCaptionsSettings () {
    const menuItemOptions = {
      kind: 'captions',
      label: this.player()[PLUGIN_NAME]().getStaticText('captionsSettings', 'Captions Settings'),
      selectable: false,
      default: false,
      mode: 'disabled',
    }
    const menuItem = new CaptionSettingsMenuItem(
      this.player(),
      menuItemOptions,
    )
    this.addItems([menuItem])
  }

  /**
   * @returns {void}
   */
  #addAutoItem () {
    const value = AUTO_MODE_VALUE

    const selectedItems = this.#menuState.getSelectedItems()
    const selected = selectedItems.includes(value)
    const menuItemOptions = {
      selectable: true,
      value,
      label: this.#pluginAPI.getStaticText(
        'autoCaptions',
        'Auto (Recommended)',
      ),
      selected,
    }

    const menuItem = new MenuItem(this.player(), menuItemOptions)
    this.addItems([menuItem])
  }

  /**
   * @returns {void}
   */
  #addOffItem () {
    const value = OFF_MODE_VALUE

    const selectedItems = this.#menuState.getSelectedItems()
    const selected = selectedItems.includes(value)

    const menuItemOptions = {
      selectable: true,
      multiSelectable: false,
      value,
      label: this.#pluginAPI.getStaticText('subtitlesOff', 'Off'),
      selected,
    }

    const menuItem = new MenuItem(this.player(), menuItemOptions)
    this.addItems([menuItem])
  }

  /**
   * @param {Object} event
   * @returns {MenuItem}
   */
  #getClickedMenuItem (event) {
    const clickedMenuItem = this.children().find((childCmt) => {
      const target = event.type === 'tap' ? event.target : event.currentTarget
      return childCmt.el() === target
    })
    return clickedMenuItem
  }

  /**
   * syncronize internal menuState with layout and run side-effects
   * @returns {void}
   */
  #updateSelection () {
    const valuesToSelect = this.#menuState.getSelectedItems()

    this.#selectMenuItems(valuesToSelect)

    this.#pluginAPI.onSubtitleLanguageChanged(valuesToSelect)
  }

  /**
   * select menu items by passed values
   * @param {string[]} values
   * @returns {void}
   */
  #selectMenuItems (values) {
    this.children().forEach((childCmt) => {
      const cmtValue = get(childCmt, 'options_.value')
      const isSelected = values.includes(cmtValue)
      childCmt.selected(isSelected)
    })
  }

  /**
   * extend super.handleTapClick:
   * 1. ignore Captions Settings
   * 2. select items
   * 3. run side-effects
   *
   * @param {Object} event
   * @returns {void}
   */
  handleTapClick (event) {
    const clickedMenuItem = this.#getClickedMenuItem(event)

    const isCaptionSettings = !isNil(clickedMenuItem)
      && clickedMenuItem instanceof CaptionSettingsMenuItem

    if (!clickedMenuItem || isCaptionSettings) {
      return
    }

    const clickedMenuItemValue = clickedMenuItem.options_.value
    this.#menuState.select(clickedMenuItemValue)

    // TODO replace with 'super.handleTapClick(event)' after updating the babel preset env
    Reflect.getPrototypeOf(
      VideoMediaCenterMenuSubtitlesColumn.prototype,
    ).handleTapClick.call(this, event)

    this.#updateSelection()
  }
}
